import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid } from '@mui/material'
import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../../app/hooks/useNotificationStack'
import { useGetCurrenciesByTenantIdQuery } from '../../../app/redux-fetch/apiOrders'
import { useRegisterCustomerMutation } from '../../../app/redux-fetch/apiQuery'
import SubmitButtons from '../../../components/Buttons/SubmitButtons'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import { SectionTitleTypography } from '../../../components/Typographies/styles'
import { useSideModalContext } from '../../Assets/modals/SideModalContext'
import { RegisterCustomerFormData, registerCustomerSchema } from './Components/registerCustomerSchema'
import messages from './messages'
import { RegisterCustomerApiError } from './types'

// E5 (WI 513/514) — the lean "register a customer under an existing Tenant" form. Captures only the
// customer name, the Account Owner's details and the required default currency (no address/location per
// §6.2). RHF + zod with mode 'onChange' so a server-set inline error on the AO email clears as soon as the
// user edits it. A 409 DUPLICATE_EMAIL_IN_SCOPE renders inline on the email field (modal stays open, values
// preserved); every other error is surfaced by the shared base-query toast.
const RegisterCustomerModal = () => {
  const { formatMessage } = useIntl()
  const { tenantId } = useLoggedInUser()
  const { onClose } = useSideModalContext()
  const { enqueueSuccess } = useNotificationStack()

  const methods = useForm<RegisterCustomerFormData>({
    resolver: zodResolver(registerCustomerSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      accountOwnerFirstName: '',
      accountOwnerLastName: '',
      accountOwnerEmail: '',
      accountOwnerPhone: '',
      defaultCurrencyId: undefined,
    },
  })

  const { data: currencies = [] } = useGetCurrenciesByTenantIdQuery(
    { tenantId, hideDisabled: true, tenantOnly: true },
    { skip: !tenantId }
  )

  // Tenant-scoped currencies only (mirrors CustomerDetail); the dropdown has no default selection (Q-E5-4).
  const currencyOptions = useMemo(
    () =>
      currencies
        ?.filter((currency: any) => !currency.customerId)
        .map((currency: any) => ({
          label: `${currency.iso3} - ${currency.localisedSymbol}`,
          value: currency.id,
        })) ?? [],
    [currencies]
  )

  const [registerCustomer, { isLoading }] = useRegisterCustomerMutation()

  // Disable Submit while the form is invalid (mode 'onChange' keeps isValid live) or a submit is in
  // flight, and Cancel while in flight — this is also the double-submit guard (the button can't be
  // re-clicked once the mutation starts).
  const busy = isLoading || methods.formState.isSubmitting

  const onSubmit = async (values: RegisterCustomerFormData) => {
    try {
      await registerCustomer({ tenantId, body: values }).unwrap()
      enqueueSuccess(messages.registerCustomerSuccess)
      onClose()
    } catch (error) {
      const apiError = error as RegisterCustomerApiError
      if (apiError?.kind === 'duplicate-email') {
        // Inline on the AO email field, exact mandated wording (not the raw backend message). The shared
        // base query suppresses the toast for this code, so this is the only surfacing.
        methods.setError('accountOwnerEmail', {
          type: 'server',
          message: formatMessage(messages.duplicateEmailInScope),
        })
        return
      }
      // other-conflict / unknown: the shared base-query toast already surfaced it; keep the modal open.
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} aria-label='register-customer-form'>
        <DefaultSpacingGrid container sx={{ p: '48px', mt: '48px', mb: '48px' }}>
          <Grid display='flex' flexDirection='column' width='100%'>
            <Box sx={{ mb: '16px' }}>
              <SectionTitleTypography>{formatMessage(messages.customerDetail)}</SectionTitleTypography>
            </Box>
            <FormInput name='name' label={formatMessage(messages.customerName)} required maxLength={250} />
            <FormSelect
              name='defaultCurrencyId'
              label={formatMessage(messages.defaultCurrency)}
              options={currencyOptions}
              required
              placeholder='Please select a default currency'
            />

            <Box sx={{ mt: '24px', mb: '16px' }}>
              <SectionTitleTypography>
                {formatMessage(messages.accountOwnerSectionTitle)}
              </SectionTitleTypography>
            </Box>
            <FormInput
              name='accountOwnerFirstName'
              label={formatMessage(messages.aoFirstName)}
              required
              maxLength={150}
            />
            <FormInput
              name='accountOwnerLastName'
              label={formatMessage(messages.aoLastName)}
              required
              maxLength={150}
            />
            <FormInput
              name='accountOwnerEmail'
              label={formatMessage(messages.aoEmail)}
              type='email'
              required
              maxLength={250}
            />
            <FormInput name='accountOwnerPhone' label={formatMessage(messages.aoPhone)} maxLength={15} />
          </Grid>
        </DefaultSpacingGrid>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            padding: '16px',
            backgroundColor: '#fff',
            borderTop: '1px solid #e0e0e0',
          }}
        >
          <SubmitButtons
            create
            isEnabled={true}
            submitDisabled={!methods.formState.isValid || busy}
            cancelDisabled={busy}
            onCancel={onClose}
            submitForm={methods.handleSubmit(onSubmit)}
          />
        </Box>
      </form>
    </FormProvider>
  )
}

export default RegisterCustomerModal
